import React, { Fragment, useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getGetStartedById, createGetStarted, updateGetStarted } from '../../actions/getStarted';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  MoveUp, 
  MoveDown,
  FileText,
  AlertCircle
} from 'lucide-react';

const initialState = {
  name: '',
  serviceType: '',
  active: false,
  content: [],
  headerText: 'How it works?',
  footerButton: {
    text: 'Got It',
    action: 'close'
  }
};

const GetStarted = ({
  getGetStartedById,
  createGetStarted,
  updateGetStarted,
  getStarted: { getStarted, loading }
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const isNewItem = id === "new";

  useEffect(() => {
    // Set document title based on whether we're editing or creating
    document.title = isNewItem ? 'Create Get Started Item' : 'Edit Get Started Item';
    
    if (!isNewItem && (!getStarted || getStarted._id !== id)) {
      getGetStartedById(id);
    }
  }, [getGetStartedById, id, isNewItem, getStarted]);

  useEffect(() => {
    if (!loading && getStarted && !isNewItem) {
      const getStartedData = { ...initialState };
      for (const key in getStarted) {
        if (key in getStartedData) {
          getStartedData[key] = getStarted[key];
        }
      }
      
      // Ensure content array is properly initialized
      if (!getStartedData.content || !Array.isArray(getStartedData.content)) {
        getStartedData.content = [];
      }
      
      // Ensure footerButton is properly initialized
      if (!getStartedData.footerButton) {
        getStartedData.footerButton = {
          text: 'Got It',
          action: 'close'
        };
      }
      
      setFormData(getStartedData);
    }
  }, [loading, getStarted, isNewItem]);

  const {
    name,
    serviceType,
    active,
    content,
    headerText,
    footerButton
  } = formData;

  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addContentStep = () => {
    const newStep = {
      step: content.length + 1,
      title: '',
      description: '',
      action: ''
    };
    setFormData(prevState => ({
      ...prevState,
      content: [...prevState.content, newStep]
    }));
  };

  const updateContentStep = (index, field, value) => {
    const updatedContent = content.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setFormData(prevState => ({
      ...prevState,
      content: updatedContent
    }));
  };

  const removeContentStep = (index) => {
    const updatedContent = content.filter((_, i) => i !== index);
    // Re-number the steps
    const renumberedContent = updatedContent.map((item, i) => ({
      ...item,
      step: i + 1
    }));
    setFormData(prevState => ({
      ...prevState,
      content: renumberedContent
    }));
  };

  const moveContentStep = (index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === content.length - 1)
    ) {
      return;
    }

    const updatedContent = [...content];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap array positions
    [updatedContent[index], updatedContent[swapIndex]] = [updatedContent[swapIndex], updatedContent[index]];
    
    // Re-number the steps
    const renumberedContent = updatedContent.map((item, i) => ({
      ...item,
      step: i + 1
    }));
    
    setFormData(prevState => ({
      ...prevState,
      content: renumberedContent
    }));
  };



  const onSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (isNewItem) {
        const newItemId = await createGetStarted(formData, navigate);
        if (newItemId) {
          navigate(`/inventory/v1.2/getstarted/${newItemId}`);
        }
      } else {
        const success = await updateGetStarted(id, formData);
        // Stay on the same page after update
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return loading && !isNewItem ? (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : (
    <Fragment>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Link to="/inventory/v1.2/getstarted" className="btn btn-outline-primary d-flex align-items-center mb-2">
              <ArrowLeft size={16} className="me-2" />
              Back to Get Started Items
            </Link>
            <h1 className="h3 mb-0 text-gray-800">
              {isNewItem ? 'Create New Get Started Item' : `Edit ${name}`}
            </h1>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              {isNewItem ? 'Create Details' : 'Edit Details'}
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={onSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={name}
                    onChange={onChange}
                    required
                    placeholder="Enter name"
                  />
                  <small className="text-muted">
                    Descriptive name for this onboarding content
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Service Type</label>
                  <select
                    className="form-select"
                    name="serviceType"
                    value={serviceType}
                    onChange={onChange}
                    required
                  >
                    <option value="">Select service type</option>
                    <option value="vending-machine">Vending Machine</option>
                    <option value="delivery">Delivery</option>
                    <option value="pod">Pod</option>
                  </select>
                  <small className="text-muted">
                    Type of service this content applies to
                  </small>
                </div>
                
                <div className="col-md-6">
                  <div className="form-check form-switch mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="active"
                      checked={active}
                      onChange={onChange}
                      id="activeSwitch"
                    />
                    <label className="form-check-label" htmlFor="activeSwitch">
                      Active
                    </label>
                  </div>
                  <small className="text-muted d-block">
                    Only active items will be shown to users
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Header Text</label>
                  <input
                    type="text"
                    className="form-control"
                    name="headerText"
                    value={headerText}
                    onChange={onChange}
                    placeholder="Enter header text"
                  />
                  <small className="text-muted">
                    Text to display as the header of the get started content
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Footer Button Text</label>
                  <input
                    type="text"
                    className="form-control"
                    name="footerButton.text"
                    value={footerButton.text}
                    onChange={(e) => {
                      setFormData(prevState => ({
                        ...prevState,
                        footerButton: {
                          ...prevState.footerButton,
                          text: e.target.value
                        }
                      }));
                    }}
                    placeholder="Enter footer button text"
                  />
                  <small className="text-muted">
                    Text to display on the footer button
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Footer Button Action</label>
                  <select
                    className="form-select"
                    name="footerButton.action"
                    value={footerButton.action}
                    onChange={(e) => {
                      setFormData(prevState => ({
                        ...prevState,
                        footerButton: {
                          ...prevState.footerButton,
                          action: e.target.value
                        }
                      }));
                    }}
                  >
                    <option value="navigate">Navigate</option>
                    <option value="close">Close</option>
                    <option value="custom">Custom</option>
                  </select>
                  <small className="text-muted">
                    Action to perform when footer button is clicked
                  </small>
                </div>
              </div>
              
              {/* Content Steps Section */}
              <hr className="my-4" />
              
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FileText size={18} className="me-2 text-primary" />
                    Content Steps
                  </h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-flex align-items-center"
                    onClick={addContentStep}
                  >
                    <Plus size={16} className="me-2" />
                    Add Step
                  </button>
                </div>
                
                {content.length === 0 ? (
                  <div className="alert alert-info d-flex align-items-center">
                    <AlertCircle size={24} className="me-3" />
                    <div>
                      No content steps added yet. Click "Add Step" to create your first step.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {content.map((step, index) => (
                      <div key={index} className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">Step {step.step}</h6>
                          <div className="btn-group">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => moveContentStep(index, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                            >
                              <MoveUp size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => moveContentStep(index, 'down')}
                              disabled={index === content.length - 1}
                              title="Move Down"
                            >
                              <MoveDown size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeContentStep(index)}
                              title="Remove Step"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">Title</label>
                              <input
                                type="text"
                                className="form-control"
                                value={step.title}
                                onChange={(e) => updateContentStep(index, 'title', e.target.value)}
                                placeholder="Enter step title"
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">Action</label>
                              <input
                                type="text"
                                className="form-control"
                                value={step.action}
                                onChange={(e) => updateContentStep(index, 'action', e.target.value)}
                                placeholder="Enter action text"
                                required
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label fw-bold">Description</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={step.description}
                                onChange={(e) => updateContentStep(index, 'description', e.target.value)}
                                placeholder="Enter step description"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <small className="text-muted d-block mt-2">
                  Steps will be displayed in order. Use the up/down buttons to reorder as needed.
                </small>
              </div>
              
              <div className="d-flex justify-content-end mt-4">
                <Link to="/inventory/v1.2/getstarted" className="btn btn-outline-secondary me-2">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-2" />
                      {isNewItem ? 'Create' : 'Update'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>


      </div>


    </Fragment>
  );
};

GetStarted.propTypes = {
  getGetStartedById: PropTypes.func.isRequired,
  createGetStarted: PropTypes.func.isRequired,
  updateGetStarted: PropTypes.func.isRequired,
  getStarted: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  getStarted: state.getStarted
});

export default connect(
  mapStateToProps,
  { getGetStartedById, createGetStarted, updateGetStarted }
)(GetStarted); 